import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, paymentApi, scheduleApi } from '../../api/endpoints.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { applyCardFee, formatBasisPointsPercent } from '@shared/calculations/payments.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { api, ApiRequestError } from '../../api/client.ts';
import { Alert, Button, ConfirmDialog, Select, TextArea, TextInput } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { AppointmentPayments } from '../payments/appointment-payments.tsx';
import { VisitPhotos } from './visit-photos.tsx';
import { AssigneePicker } from './assignee-picker.tsx';
import { AvailabilityPicker } from './availability-picker.tsx';
import { formatDuration } from './format.ts';
import { formatLocalTime, formatShortDate } from '../../i18n/datetime.ts';
import { acceptProposedTime, ExplainedRefusal, proposalHasPassed, proposedTimeLabel } from './change-request.ts';
import { useSession } from '../../state/session.tsx';
import type { Appointment } from '@shared/types/index.ts';
import { OPEN_APPOINTMENT_STATUSES } from '@shared/constants/index.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';

/**
 * The per-visit action row: confirm on the customer's behalf, move the visit
 * (with the crew's availability drawn in), assign its crew, cancel it, close
 * it out (optionally recording how long it really took and correcting what it
 * costs), or copy the customer's confirmation link. Shared by the calendar's
 * detail panel and the series page's visit list.
 */
/** What a late-fee question named, taken once when it was asked and sent as asked. */
interface LateFeeFigures {
  /** What is still owed: the fee invoice's balance, or the fee itself. */
  baseCents: number;
  /** The card total, once the card fee is known. */
  totalCents: number | null;
  card: { last4: string; brand?: string } | null;
}

export function AppointmentActions({
  appointment,
  onChanged,
  startCompleting = false,
  startCanceling = false,
}: {
  appointment: Appointment;
  onChanged: () => void;
  /**
   * Open already on the close-out form — used when a "visit still open"
   * notification (or any other deep link with `?complete=1`) brings someone
   * here to finish the job, not to browse it.
   */
  startCompleting?: boolean;
  /** Cancellation deep links open the existing confirmation without submitting it. */
  startCanceling?: boolean;
}) {
  const { t } = useTranslation();
  const { can, company, currencyCode: currency } = useSession();
  const queryClient = useQueryClient();
  const canTakePayment = can('payments:write');
  const [error, setError] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [moveDate, setMoveDate] = useState(appointment.scheduledDate);
  const [moveTime, setMoveTime] = useState(appointment.startTimeLocal);
  const [actualMinutes, setActualMinutes] = useState('');
  const [photoOverride, setPhotoOverride] = useState(false);
  const [photoOverrideReason, setPhotoOverrideReason] = useState('');
  const completionChecklist = useQuery({
    queryKey: ['visit-checklist', appointment.id],
    queryFn: () => scheduleApi.checklist(appointment.id),
    enabled: completing,
    refetchInterval: completing ? 5000 : false,
  });
  const missingPhotos = completionChecklist.data?.missingRequiredPhotos ?? 0;
  const photosBlocked = Boolean(completionChecklist.data?.blockCompletionOnMissingPhotos && missingPhotos > 0);
  const photoCheckFailed =
    completionChecklist.isError &&
    !(completionChecklist.error instanceof ApiRequestError && completionChecklist.error.status === 404);
  const canOverridePhotos = can('checklists:override');
  useEffect(() => {
    setPhotoOverride(false);
    setPhotoOverrideReason('');
  }, [appointment.id, completing]);
  /**
   * What the visit costs, as the completion dialog has it. Seeded from the
   * visit's own price when the dialog opens, so leaving it alone charges what
   * the schedule said — the field is a correction, not a second decision to
   * make on every visit.
   */
  const [priceCents, setPriceCents] = useState<number | null>(null);
  /**
   * Whether anybody has actually touched that field.
   *
   * What gets sent is decided by this rather than by comparing the box against
   * the visit's price, because the visit's price can move underneath an open
   * dialog — the series page reprices future visits from a card on the same
   * screen — and a comparison would then read the seeded, untouched figure as
   * a correction and send it back, undoing the newer price on the way to
   * billing it.
   */
  const [priceEdited, setPriceEdited] = useState(false);
  /**
   * The box holds something that is not an amount at all.
   *
   * Distinct from empty, which reports the same `null`: emptying the field
   * means "no correction, use the schedule's price", while "1e3" means
   * somebody typed a number the parser refuses to guess at. Completing on the
   * first is right; completing on the second would charge the old amount
   * under an entry that is still on screen.
   */
  const [priceUnreadable, setPriceUnreadable] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canceling, setCanceling] = useState(
    startCanceling && can('schedule:write') && OPEN_APPOINTMENT_STATUSES.includes(appointment.status),
  );
  const [cancellationInitiator, setCancellationInitiator] = useState<'customer' | 'company' | 'unknown'>('unknown');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  // A no-show closes the visit for good — there is no reopening it from here —
  // and the button sits in a row that wraps on a phone, so it asks first.
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const [noShowError, setNoShowError] = useState<string | null>(null);
  // The crew as the office last set it, held locally: each checkbox mutates
  // immediately, and computing the next crew from the parent's still-stale
  // prop during the refetch gap would let a second quick click erase the
  // first. Synced back whenever the server's answer arrives.
  const [teamId, setTeamId] = useState<string | null>(appointment.teamId ?? null);
  const [crew, setCrew] = useState<string[]>(appointment.assignedUserIds);
  // Keyed on who is on the visit rather than on the array: the calendar
  // refetches on a timer, and a fresh copy of the same crew must not wipe a
  // change the office has not yet said where to apply.
  const savedCrewKey = appointment.assignedUserIds.join(',');
  /**
   * The pick last saved from the scope question, until the visit read back
   * shows it. Without it the question would come back, live, in the gap
   * before that read — and a second answer would write a second time.
   */
  const [savedPick, setSavedPick] = useState<string | null>(null);
  useEffect(() => {
    setCrew(appointment.assignedUserIds);
    setTeamId(appointment.teamId ?? null);
    setSavedPick(null);
  }, [appointment.id, savedCrewKey, appointment.teamId]);

  const isOpen = OPEN_APPOINTMENT_STATUSES.includes(appointment.status);
  /**
   * The schedule behind the visit, read to tell whether it repeats. A crew
   * change on a repeating schedule is held until the office says whether it
   * is for this visit only or for every visit ahead; anything else saves as
   * it is picked. The series page has it cached under the same key.
   */
  const series = useQuery({
    queryKey: ['series', appointment.seriesId],
    queryFn: () => scheduleApi.series(appointment.seriesId),
    enabled: isOpen && Boolean(appointment.seriesId),
  });
  // A schedule that could not be read is asked about too: saving to this
  // visit alone, unasked, is the one answer that can be wrong unnoticed.
  //
  // A pick held for the question stays a question until it is answered,
  // even if a later read finds the schedule does not repeat: dropping the
  // question then would leave the pick on screen, unsaved, with no way to
  // save it. Once answered, the next pick goes by what the schedule is.
  const [asked, setAsked] = useState(false);
  useEffect(() => setAsked(false), [appointment.id]);
  const repeats =
    asked ||
    series.isError ||
    Boolean(series.data && series.data.series.frequency !== 'one_time' && series.data.series.status !== 'ended');
  const crewChanged = teamId !== (appointment.teamId ?? null) || crew.join(',') !== savedCrewKey;
  const pickKey = `${teamId ?? ''}|${crew.join(',')}`;
  const fee = appointment.lateCancelFee;
  const feeAmount =
    fee?.amountCents != null && fee.amountCents > 0
      ? formatCents(fee.amountCents, fee.currencyCode ?? currency, getDateLocale())
      : null;
  const feeApplies = Boolean(fee?.applies && feeAmount);
  const feePending = appointment.status === 'cancelled' && fee?.status === 'pending' && Boolean(feeAmount);
  const customer = useQuery({
    queryKey: ['customer', appointment.customerId, 'late-fee-card'],
    queryFn: () => customerApi.get(appointment.customerId),
    enabled: Boolean(canTakePayment && feeApplies && (canceling || feePending)),
  });
  const paymentStatus = useQuery({
    queryKey: ['payment', 'status'],
    queryFn: () => paymentApi.status(),
    enabled: Boolean(canTakePayment && feeApplies && (canceling || feePending)),
  });
  const customerHasCard = Boolean(customer.data?.cardOnFile);
  const noCard = customer.isSuccess ? customer.data.cardOnFile == null : false;
  const customerPayments = useQuery({
    // Priced at this visit's location, where its late fee is billed — the
    // customer's own location can carry a different card fee.
    queryKey: ['customer-payments', appointment.customerId, 'late-fee-preview', appointment.id],
    queryFn: () => paymentApi.customerPayments(appointment.customerId, { pageSize: 1, appointmentId: appointment.id }),
    enabled: Boolean(canTakePayment && feeApplies && (canceling || feePending) && customerHasCard),
  });
  const feeInvoiceId = fee?.invoiceId ?? null;
  const watchFeeInvoice = Boolean(canTakePayment && feeInvoiceId && (feePending || fee?.status === 'charging'));
  const feeInvoiceLedger = useQuery({
    queryKey: ['invoice-payments', feeInvoiceId, 'late-fee'],
    queryFn: () => paymentApi.invoicePayments(feeInvoiceId!),
    enabled: watchFeeInvoice,
  });
  const outstandingCheckoutLink = Boolean(
    feeInvoiceLedger.data?.items.some(
      (row) => row.type === 'charge' && row.method === 'checkout_link' && row.status === 'pending',
    ),
  );
  const feeInvoiceLedgerReady = !watchFeeInvoice || feeInvoiceLedger.isFetched;
  const companyCanText = Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled);
  // What charging the fee takes now: once it has an invoice, that invoice's
  // remaining balance (a payment link may have paid part of it), which is
  // what the server charges; before then, the fee itself.
  // The fee is charged in the currency it was recorded in, whatever the
  // location bills new work in now.
  const feeCurrency = fee?.currencyCode ?? currency;
  const feeBaseCents = (watchFeeInvoice ? feeInvoiceLedger.data?.balanceCents : undefined) ?? fee?.amountCents ?? 0;
  const cardOnFileFeeBasisPoints =
    customerPayments.data?.cardFeeBasisPoints ?? paymentStatus.data?.cardFeeBasisPoints ?? 0;
  const cardFeePreview =
    feeBaseCents > 0
      ? applyCardFee(feeBaseCents, { enabled: true, basisPoints: cardOnFileFeeBasisPoints }, feeCurrency)
      : null;
  const linkFeePreview =
    feeBaseCents > 0
      ? applyCardFee(
          feeBaseCents,
          { enabled: true, basisPoints: paymentStatus.data?.cardFeeBasisPoints ?? 0 },
          feeCurrency,
        )
      : null;
  const cardFeeDisclosureKnown =
    paymentStatus.data !== undefined && customer.isSuccess && (noCard || customerPayments.data !== undefined);
  const paymentsConfigured = paymentStatus.data?.configured === true;

  function paymentLinkDelivery(): { sendEmail: boolean; sendText: boolean } {
    const profile = customer.data;
    if (!profile) return { sendEmail: false, sendText: false };
    const sendEmail = Boolean(profile.email);
    const sendText = Boolean(
      companyCanText && profile.smsConsentStatus === 'opted_in' && profile.smsReachable && profile.phone,
    );
    return { sendEmail, sendText };
  }

  const chargeButtonAmount =
    cardFeeDisclosureKnown && cardFeePreview && cardFeePreview.totalCents > 0
      ? formatCents(cardFeePreview.totalCents, fee?.currencyCode ?? currency, getDateLocale())
      : feeAmount;

  /** What the office sees as the collectable total once card-fee previews have loaded. */
  const lateFeeDisclosedAmount = ((): string | null => {
    if (!feeAmount || !canTakePayment || !cardFeeDisclosureKnown) return feeAmount;
    if (noCard && linkFeePreview && linkFeePreview.totalCents > 0) {
      return formatCents(linkFeePreview.totalCents, fee?.currencyCode ?? currency, getDateLocale());
    }
    if (!noCard && cardFeePreview && cardFeePreview.totalCents > 0) {
      return formatCents(cardFeePreview.totalCents, fee?.currencyCode ?? currency, getDateLocale());
    }
    return feeAmount;
  })();

  function lateFeeCardChargeNotice(): ReactNode {
    if (!paymentsConfigured || !cardFeeDisclosureKnown || !cardFeePreview || noCard) return null;
    return (
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {cardFeePreview.feeCents > 0
          ? t('appointmentPayments.cardOnFileFeeNotice', {
              percent: formatBasisPointsPercent(cardFeePreview.basisPoints),
              total: formatCents(cardFeePreview.totalCents, feeCurrency, getDateLocale()),
            })
          : t('appointmentPayments.cardOnFileNoFeeNotice', {
              total: formatCents(cardFeePreview.totalCents, feeCurrency, getDateLocale()),
            })}
      </p>
    );
  }

  // Charging a late fee and waiving one are both final — a waived fee cannot be
  // charged later — and each is one tap beside the other. So each asks first,
  // with the amount in the question, and the card when its digits are known.
  //
  // The figures are taken once, when the question is asked, and the same
  // snapshot is what the request sends: nothing read after the answer — a
  // refetch landing before the request goes out — can change what was agreed.
  function lateFeeFigures(): LateFeeFigures {
    const card = customer.data?.cardOnFile;
    return {
      baseCents: feeBaseCents,
      totalCents: cardFeeDisclosureKnown && cardFeePreview ? cardFeePreview.totalCents : null,
      card: card?.last4 ? { last4: card.last4, ...(card.brand ? { brand: card.brand } : {}) } : null,
    };
  }
  /**
   * The figures the charge question named, once answered yes; null otherwise.
   * Nothing owed is no question to ask: a charge or a waiver always names its
   * figure, so the server can refuse one that moved.
   */
  function askChargeLateFee(): LateFeeFigures | null {
    const figures = lateFeeFigures();
    if (figures.baseCents <= 0) return null;
    const amount =
      figures.totalCents !== null && figures.totalCents > 0
        ? formatCents(figures.totalCents, fee?.currencyCode ?? currency, getDateLocale())
        : feeAmount;
    const confirmed = window.confirm(
      figures.card
        ? t('appointmentActions.confirmChargeLateFeeCard', { amount, last4: figures.card.last4 })
        : t('appointmentActions.confirmChargeLateFee', { amount }),
    );
    return confirmed ? figures : null;
  }
  /** The figures the waive question named, once answered yes; null otherwise. */
  function askWaiveLateFee(): LateFeeFigures | null {
    const figures = lateFeeFigures();
    if (figures.baseCents <= 0) return null;
    // What waiving forgives is what is still owed: a part-paid fee keeps what
    // was already collected.
    const owed = formatCents(figures.baseCents, fee?.currencyCode ?? currency, getDateLocale());
    return window.confirm(t('appointmentActions.confirmWaiveLateFee', { amount: owed })) ? figures : null;
  }

  function lateFeeLinkNotice(): ReactNode {
    if (!paymentsConfigured || !cardFeeDisclosureKnown || !linkFeePreview) return null;
    if (linkFeePreview.feeCents <= 0) return null;
    return (
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {t('appointmentPayments.cardFeeNotice', {
          percent: formatBasisPointsPercent(linkFeePreview.basisPoints),
          fee: formatCents(linkFeePreview.feeCents, feeCurrency, getDateLocale()),
          total: formatCents(linkFeePreview.totalCents, feeCurrency, getDateLocale()),
        })}
      </p>
    );
  }

  /** The time the customer asked for, in words, or null when they typed a message instead. */
  const proposed = proposedTimeLabel(appointment);
  // Asked at render to decide what to draw; `acceptProposedTime` asks again at
  // the click, because a panel left open across the deadline never re-renders.
  const proposedHasPassed = proposalHasPassed(appointment);

  /**
   * Whether the visit's stored price is money the company still bills in.
   *
   * A price left behind by a currency change is a bare number in another
   * currency, so it cannot be offered under today's symbol — automatic
   * billing refuses to charge one of those for the same reason, and asks for
   * the amount in today's money.
   */
  const pricedInCurrentCurrency = appointment.priceCurrencyCode === null || appointment.priceCurrencyCode === currency;

  /**
   * What is wrong with the box, if anything — the two ways an entry can be no
   * kind of correction at all.
   *
   * Both matter here because of what the field's neighboring values mean.
   * Unreadable text reports the same `null` as an empty box, and empty means
   * "bill what the schedule said". A negative reads as zero, and zero means
   * "free of charge" — so `-10`, which is somebody reaching for a ten-dollar
   * discount, would otherwise close the visit out for nothing. Neither may be
   * quietly rounded into the instruction next door.
   */
  const priceProblem = priceUnreadable
    ? t('appointmentActions.priceUnreadable')
    : priceCents !== null && priceCents < 0
      ? t('appointmentActions.priceNegative')
      : null;

  /**
   * The price to send, or nothing when the office left the figure alone.
   *
   * Only an edit sends anything, and only a sound one. An emptied field is
   * not "free of charge" — it is a field nobody filled in — so it sends
   * nothing and the visit keeps the price its schedule gave it; zero typed as
   * zero is free of charge, and does send. Typing the same digits the box
   * opened with still counts, which is what restamps a stale-currency price.
   */
  const correctedPrice = priceEdited && priceCents !== null && priceCents >= 0 ? priceCents : undefined;
  /**
   * A visit booked from a quote sent as a price range has no price until it
   * is done: the range promised the final one at the visit. Completing it
   * waits for that figure, as the server does.
   */
  const finalPriceNeeded = appointment.finalPriceNeeded === true;

  // Every field in the completion dialog belongs to the visit it was opened
  // for. This panel stays mounted when the visit around it changes, and a
  // leftover here is not a stale number somebody notices — it is an amount
  // that closes out and charges the wrong visit.
  //
  // A deep link that asked for the form (`startCompleting`) seeds it the
  // same way the Complete button does, so the first thing on screen is the
  // close-out — not another click to get there. Only the visit id and that
  // intent re-run this: a background refetch of the same visit must not
  // wipe minutes or a price mid-edit.
  useEffect(() => {
    setActualMinutes('');
    setPriceCents(null);
    setPriceEdited(false);
    setPriceUnreadable(false);
    if (startCompleting && OPEN_APPOINTMENT_STATUSES.includes(appointment.status)) {
      setActualMinutes(String(appointment.durationMinutes));
      setPriceCents(pricedInCurrentCurrency ? appointment.priceCents : null);
      setCompleting(true);
      return;
    }
    setCompleting(false);
    // pricedInCurrentCurrency / status / duration / price are read once when
    // the visit (or deep-link intent) changes; listing them would re-seed the
    // form on every parent refetch of the same visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [appointment.id, startCompleting]);

  const shared = {
    onSuccess: () => {
      setError(null);
      setRescheduling(false);
      setCompleting(false);
      onChanged();
    },
    // An `ApiRequestError` carries a sentence the server wrote for a person,
    // and an `ExplainedRefusal` one this component did. Everything else is a
    // fault rather than an answer — a TypeError from a bad render is not
    // something to put in front of somebody — and gets the generic line.
    //
    // Without the second case the two proposal races threw plain `Error`s
    // and were shown as "Could not update this visit", which is the one
    // thing that had NOT happened: the strings explaining them were written
    // for exactly these moments and never reached anybody.
    onError: (caught: unknown) =>
      setError(
        caught instanceof ApiRequestError || caught instanceof ExplainedRefusal
          ? caught.message
          : t('appointmentActions.updateError'),
      ),
  };

  const confirm = useMutation({
    mutationFn: () => scheduleApi.confirm(appointment.id),
    ...shared,
  });
  const cancel = useMutation({
    mutationFn: (decision?: { action: 'charge' | 'waive'; figures: LateFeeFigures }) => {
      const figures = decision?.figures;
      return scheduleApi.cancelAppointment(appointment.id, {
        cancellationInitiator,
        ...(cancellationReason.trim() ? { reason: cancellationReason.trim() } : {}),
        ...(decision ? { lateFeeAction: decision.action } : {}),
        // The fee the question named, not whatever Settings says by the time
        // the request arrives. The server refuses a charge or a waiver if it no
        // longer matches.
        ...(decision?.action === 'waive' && figures ? { lateFeeAmountCents: figures.baseCents } : {}),
        ...(decision?.action === 'charge' && figures
          ? {
              lateFeeAmountCents: figures.baseCents,
              lateFeeChargeTotalCents: figures.totalCents ?? figures.baseCents,
              ...(figures.card ? { lateFeeCardLast4: figures.card.last4 } : {}),
              ...(figures.card?.brand ? { lateFeeCardBrand: figures.card.brand } : {}),
            }
          : {}),
      });
    },
    onSuccess: (updated) => {
      setCanceling(false);
      setCancellationInitiator('unknown');
      setCancellationReason('');
      setCancelError(null);
      shared.onSuccess();
      if (updated.lateCancelFee?.lastError) setError(updated.lateCancelFee.lastError);
    },
    onError: (caught: unknown) => {
      setCancelError(caught instanceof ApiRequestError ? caught.message : t('appointmentActions.updateError'));
      refreshLateFeeFigures();
      // The fee the question named comes from the visit itself, so its owner
      // reads it again: a refusal over a changed fee then asks the new one.
      onChanged();
    },
  });
  /**
   * After a refusal — most often a figure that moved since this screen read it
   * — every source of the late-fee question reads again: the fee invoice's
   * balance, the card fee, and the card itself. Only those already in use.
   */
  // Held while they read again, so the next attempt asks the current figures.
  const lateFeeFiguresRefreshing =
    feeInvoiceLedger.isRefetching ||
    paymentStatus.isRefetching ||
    customer.isRefetching ||
    customerPayments.isRefetching;
  function refreshLateFeeFigures() {
    if (watchFeeInvoice) void feeInvoiceLedger.refetch();
    if (paymentStatus.isFetched) void paymentStatus.refetch();
    if (customer.isFetched) void customer.refetch();
    if (customerPayments.isFetched) void customerPayments.refetch();
  }
  const feeError = (caught: unknown) =>
    setError(caught instanceof ApiRequestError ? caught.message : t('appointmentActions.updateError'));
  const chargeFee = useMutation({
    // The fee, the card total and the card the question named: the server
    // refuses a charge that would now come to anything else.
    mutationFn: (figures: LateFeeFigures) =>
      scheduleApi.chargeLateFee(appointment.id, {
        expectedAmountCents: figures.baseCents,
        ...(figures.totalCents !== null ? { expectedTotalCents: figures.totalCents } : {}),
        ...(figures.card ? { expectedCardLast4: figures.card.last4 } : {}),
        ...(figures.card?.brand ? { expectedCardBrand: figures.card.brand } : {}),
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (caught: unknown) => {
      feeError(caught);
      refreshLateFeeFigures();
    },
  });
  const waiveFee = useMutation({
    // What the question named as still owed: a fee that moved since (a
    // payment link paid part of it) is refused rather than waived unseen.
    mutationFn: (figures: LateFeeFigures) =>
      scheduleApi.waiveLateFee(appointment.id, { expectedAmountCents: figures.baseCents }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (caught: unknown) => {
      feeError(caught);
      refreshLateFeeFigures();
    },
  });
  const sendPaymentLink = useMutation({
    mutationFn: async (cancelFirst: boolean) => {
      const delivery = paymentLinkDelivery();
      if (!delivery.sendEmail && !delivery.sendText) {
        throw new Error(t('appointmentActions.lateFeeNoPaymentContact'));
      }
      let invoiceId = appointment.lateCancelFee?.invoiceId ?? null;
      if (cancelFirst) {
        const canceled = await scheduleApi.cancelAppointment(appointment.id, {
          cancellationInitiator,
          ...(cancellationReason.trim() ? { reason: cancellationReason.trim() } : {}),
        });
        invoiceId = canceled.lateCancelFee?.invoiceId ?? null;
        if (canceled.lateCancelFee?.status === 'pending' && !invoiceId) {
          const withInvoice = await scheduleApi.ensureLateFeeInvoice(appointment.id);
          invoiceId = withInvoice.lateCancelFee.invoiceId;
        }
      } else if (!invoiceId) {
        const withInvoice = await scheduleApi.ensureLateFeeInvoice(appointment.id);
        invoiceId = withInvoice.lateCancelFee.invoiceId;
      }
      if (!invoiceId) throw new Error(t('appointmentActions.lateFeeNoInvoice'));
      await paymentApi.requestInvoicePayment(invoiceId, delivery);
    },
    onSuccess: () => {
      setCanceling(false);
      setError(null);
      onChanged();
    },
    onError: (caught: unknown) => {
      const message =
        caught instanceof ApiRequestError || caught instanceof Error
          ? caught.message
          : t('appointmentActions.updateError');
      setCancelError(message);
      setError(message);
    },
  });
  const cancelDialogBusy = cancel.isPending || sendPaymentLink.isPending;
  const complete = useMutation({
    mutationFn: () => {
      const minutes = Number.parseInt(actualMinutes, 10);
      const duration = Number.isInteger(minutes) && minutes > 0 ? minutes : null;
      return photosBlocked && photoOverride
        ? scheduleApi.complete(
            appointment.id,
            duration,
            correctedPrice,
            photoOverrideReason.trim(),
            completionChecklist.data?.revision,
          )
        : scheduleApi.complete(appointment.id, duration, correctedPrice, undefined, completionChecklist.data?.revision);
    },
    ...shared,
  });
  const noShow = useMutation({
    mutationFn: () => scheduleApi.noShow(appointment.id),
    onSuccess: () => {
      setMarkingNoShow(false);
      setNoShowError(null);
      shared.onSuccess();
    },
    onError: (caught: unknown) =>
      setNoShowError(caught instanceof ApiRequestError ? caught.message : t('appointmentActions.updateError')),
  });
  const move = useMutation({
    mutationFn: () =>
      scheduleApi.reschedule(appointment.id, {
        scheduledDate: moveDate,
        startTimeLocal: moveTime,
      }),
    ...shared,
  });
  /** The customer picked a time off their own page; this accepts it (see `acceptProposedTime`). */
  const acceptProposal = useMutation({
    mutationFn: () =>
      acceptProposedTime(
        appointment,
        {
          passed: t('appointmentActions.requestedTimeHasPassedShort'),
          handled: t('appointmentActions.requestAlreadyHandled'),
        },
        onChanged,
      ),
    ...shared,
  });
  const assign = useMutation({
    mutationFn: (assignedUserIds: string[]) => scheduleApi.reschedule(appointment.id, { assignedUserIds }),
    ...shared,
    onError: (caught: unknown) => {
      // Roll the optimistic crew back to what the server last confirmed.
      setCrew(appointment.assignedUserIds);
      shared.onError(caught);
    },
  });

  const assignTeam = useMutation({
    mutationFn: (body: { teamId: string | null; assignedUserIds: string[] }) =>
      scheduleApi.reschedule(appointment.id, body),
    ...shared,
    onError: (caught: unknown) => {
      setCrew(appointment.assignedUserIds);
      setTeamId(appointment.teamId ?? null);
      shared.onError(caught);
    },
  });

  /**
   * The crew on screen, for this visit and every one after it.
   *
   * The schedule's own crew changes, and the server carries it to each visit
   * still ahead. This visit is then read back: one already underway, or on a
   * weekday that names its own crew, is left as it was by that rewrite — and
   * it is the visit the change was asked from, so it gets the crew directly.
   * A change that does not apply is kept on screen with the error, so the
   * office can still choose this visit only.
   *
   * The two writes are not one transaction. Once the schedule's has landed it
   * cannot be taken back from here, so a failure after it says so and
   * refreshes, rather than reading as if nothing had changed.
   */
  const assignFuture = useMutation({
    mutationFn: async () => {
      const body = { teamId, assignedUserIds: crew };
      await scheduleApi.updateSeries(appointment.seriesId, body);
      void queryClient.invalidateQueries({ queryKey: ['series', appointment.seriesId] });
      const partly = (key: string, caught: unknown) => {
        onChanged();
        return new ExplainedRefusal(
          t(key, {
            reason: caught instanceof ApiRequestError ? caught.message : t('appointmentActions.updateError'),
          }),
        );
      };
      // Not read back is not the same as not changed: the schedule's rewrite
      // has most likely reached this visit too, so it is checked, not denied.
      const current = await scheduleApi.appointment(appointment.id).catch((caught: unknown) => {
        throw partly('appointmentActions.crewScopeVisitUnchecked', caught);
      });
      if (current.assignedUserIds.join(',') !== crew.join(',') || (current.teamId ?? null) !== teamId) {
        await scheduleApi.reschedule(appointment.id, body).catch((caught: unknown) => {
          throw partly('appointmentActions.crewScopeVisitFailed', caught);
        });
      }
    },
    ...shared,
    // A change that did not fully apply stays held, still asking.
    onSuccess: () => {
      setAsked(false);
      setSavedPick(pickKey);
      shared.onSuccess();
    },
  });

  function assignThisVisitOnly() {
    // Answered: a refusal rolls back to the saved crew, so nothing is left held.
    setAsked(false);
    const saved = { onSuccess: () => setSavedPick(pickKey) };
    if (teamId !== (appointment.teamId ?? null)) assignTeam.mutate({ teamId, assignedUserIds: crew }, saved);
    else assign.mutate(crew, saved);
  }

  const keepTeam = useMutation({
    mutationFn: () => api.post(`/customers/${appointment.customerId}/keep-team/${appointment.id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', appointment.customerId] });
      onChanged();
    },
    onError: shared.onError,
  });

  async function copyLink() {
    if (!appointment.confirmPath) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${appointment.confirmPath}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('appointmentActions.copyError'));
    }
  }

  const workOrderLink = (
    <Link
      to={`/schedule/visits/${appointment.id}/work-order`}
      className="text-[color:var(--color-brand)] hover:underline"
    >
      {t('appointmentActions.workOrder')}
    </Link>
  );

  const lateFeeNotice =
    feeAmount && fee?.noticeHours
      ? t('appointmentActions.lateFeeApplies', {
          count: fee.noticeHours,
          amount: lateFeeDisclosedAmount ?? feeAmount,
        })
      : null;

  if (!isOpen) {
    // Closed visits still have a money story — completed is exactly when the
    // office charges — and their photos are the record of the work.
    return (
      <div>
        {error ? (
          <div className="mb-3">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {can('customers:write') && can('schedule:write') && appointment.keepTeamPrompt ? (
          <div className="mb-3 rounded-lg border p-3">
            <p>{t('savedTeams.keepPrompt', { team: appointment.keepTeamPrompt, client: appointment.customerName })}</p>
            <Button busy={keepTeam.isPending} onClick={() => keepTeam.mutate()}>
              {t('savedTeams.keep')}
            </Button>
          </div>
        ) : null}
        {feePending && lateFeeNotice ? (
          <div className="mb-3 rounded-lg border border-[color:var(--color-line)] p-3">
            <p className="mb-3 text-sm">{lateFeeNotice}</p>
            {canTakePayment ? (
              noCard ? (
                <>
                  <p className="mb-3 text-sm">{t('appointmentActions.lateFeeNoCard')}</p>
                  {lateFeeLinkNotice()}
                  <div className="flex flex-wrap items-center gap-2">
                    {outstandingCheckoutLink ? (
                      <p className="text-sm">{t('appointmentActions.lateFeePaymentLinkPending')}</p>
                    ) : paymentsConfigured ? (
                      <Button
                        busy={sendPaymentLink.isPending}
                        disabled={!feeInvoiceLedgerReady}
                        onClick={() => sendPaymentLink.mutate(false)}
                      >
                        {t('appointmentActions.sendPaymentLink')}
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      busy={waiveFee.isPending}
                      // Held until the fee invoice says what is still owed, which is
                      // what the question names and the waiver is bound to.
                      disabled={!feeInvoiceLedgerReady || lateFeeFiguresRefreshing || feeBaseCents <= 0}
                      onClick={() => {
                        const figures = askWaiveLateFee();
                        if (figures) waiveFee.mutate(figures);
                      }}
                    >
                      {t('appointmentActions.waiveFee')}
                    </Button>
                    <Link to="/invoices" className="text-[color:var(--color-brand)] hover:underline">
                      {t('appointmentActions.openInvoices')}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  {lateFeeCardChargeNotice()}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      busy={chargeFee.isPending}
                      disabled={
                        !feeAmount ||
                        !cardFeeDisclosureKnown ||
                        !paymentsConfigured ||
                        !feeInvoiceLedgerReady ||
                        lateFeeFiguresRefreshing ||
                        feeBaseCents <= 0
                      }
                      onClick={() => {
                        const figures = askChargeLateFee();
                        if (figures) chargeFee.mutate(figures);
                      }}
                    >
                      {t('appointmentActions.chargeLateFee', { amount: chargeButtonAmount ?? feeAmount })}
                    </Button>
                    <Button
                      variant="ghost"
                      busy={waiveFee.isPending}
                      // Held until the fee invoice says what is still owed, which is
                      // what the question names and the waiver is bound to.
                      disabled={!feeInvoiceLedgerReady || lateFeeFiguresRefreshing || feeBaseCents <= 0}
                      onClick={() => {
                        const figures = askWaiveLateFee();
                        if (figures) waiveFee.mutate(figures);
                      }}
                    >
                      {t('appointmentActions.waiveFeeShort')}
                    </Button>
                  </div>
                </>
              )
            ) : (
              <p className="text-sm text-[color:var(--color-muted)]">
                {t('appointmentActions.lateFeeNeedsPaymentRole')}
              </p>
            )}
          </div>
        ) : null}
        {fee?.status === 'charged' && feeAmount ? (
          <p className="mb-3 text-sm">{t('appointmentActions.lateFeeCharged', { amount: feeAmount })}</p>
        ) : null}
        {fee?.status === 'waived' ? (
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('appointmentActions.lateFeeWaived')}</p>
        ) : null}
        {fee?.status === 'charging' ? (
          <p className="mb-3 text-sm">
            {outstandingCheckoutLink
              ? t('appointmentActions.lateFeePaymentLinkPending')
              : t('appointmentActions.lateFeeCharging')}
          </p>
        ) : null}
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('appointmentActions.closedMessage')}
          {appointment.actualMinutes ? (
            <>
              {' '}
              {t('appointmentActions.tookTime', {
                time: formatDuration(appointment.actualMinutes),
              })}
            </>
          ) : null}{' '}
          {workOrderLink}
        </p>
        <AppointmentPayments appointment={appointment} onChanged={onChanged} />
        <VisitPhotos appointmentId={appointment.id} />
      </div>
    );
  }

  return (
    <div>
      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {appointment.changeRequestMessage ? (
        <div className="mb-3">
          <Alert tone="warning" title={t('appointmentActions.changeRequestedTitle')}>
            <p>{appointment.changeRequestMessage}</p>
            {/* A time the customer picked off the slot list is a decision
                waiting on one click, so it gets a button rather than sending
                the office to the move form to retype what the message
                already says. A typed message has no time to accept and keeps
                the form it always had. */}
            {proposed ? (
              <div className="mt-2">
                {proposedHasPassed ? (
                  // The hour still gets said — it is what the customer asked
                  // for and the office has to answer for it — but not as
                  // something one click can still do.
                  <p className="text-sm text-[color:var(--color-muted)]">
                    {t('appointmentActions.requestedTimeHasPassed', { when: proposed })}
                  </p>
                ) : (
                  <Button variant="primary" busy={acceptProposal.isPending} onClick={() => acceptProposal.mutate()}>
                    {t('appointmentActions.moveToRequestedTime', { when: proposed })}
                  </Button>
                )}
              </div>
            ) : null}
          </Alert>
        </div>
      ) : null}

      {appointment.teamFlexible ? <p className="mb-3 text-sm">{t('savedTeams.flexible')}</p> : null}
      <div className="mb-3">
        <AssigneePicker
          label={t('appointmentActions.assigneeLabel')}
          value={crew}
          teamId={teamId}
          onTeamChange={(id, members) => {
            setTeamId(id);
            setCrew(members);
            if (repeats) setAsked(true);
            else assignTeam.mutate({ teamId: id, assignedUserIds: members });
          }}
          // Held while the schedule is still being read: a pick made before
          // it is known would save without asking where it applies.
          disabled={assign.isPending || assignTeam.isPending || assignFuture.isPending || series.isLoading}
          onChange={(next) => {
            setCrew(next);
            if (repeats) setAsked(true);
            else assign.mutate(next);
          }}
        />
        {/* Live, because the pick that raised the question is not saved until
            it is answered — and the checkbox alone reads as if it were. */}
        <div aria-live="polite">
          {repeats && crewChanged && pickKey !== savedPick ? (
            <div className="rounded-lg border border-[color:var(--color-line)] p-3">
              <p className="mb-2 text-sm">{t('appointmentActions.crewScopeQuestion')}</p>
              <div className="flex flex-wrap gap-2">
                {/* The narrower answer is the emphasized one: a thumb that lands
                    on it by mistake changes one visit, not the whole series. */}
                <Button
                  variant="primary"
                  busy={assign.isPending || assignTeam.isPending}
                  disabled={assignFuture.isPending}
                  onClick={assignThisVisitOnly}
                >
                  {t('appointmentActions.crewScopeThisVisit')}
                </Button>
                <Button
                  busy={assignFuture.isPending}
                  disabled={assign.isPending || assignTeam.isPending}
                  onClick={() => assignFuture.mutate()}
                >
                  {t('appointmentActions.crewScopeAllFuture')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={assign.isPending || assignTeam.isPending || assignFuture.isPending}
                  onClick={() => {
                    setAsked(false);
                    setCrew(appointment.assignedUserIds);
                    setTeamId(appointment.teamId ?? null);
                  }}
                >
                  {t('appointmentActions.keepAsIs')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {rescheduling ? (
        <div className="mb-3 rounded-lg border border-[color:var(--color-line)] p-3">
          {/* The crew's week, with this visit's own hour left out so it does
              not block its own move. A click lands in the fields below. */}
          <AvailabilityPicker
            userIds={appointment.assignedUserIds}
            durationMinutes={appointment.durationMinutes}
            selected={{ date: moveDate, time: moveTime }}
            excludeAppointmentId={appointment.id}
            onPick={(date, time) => {
              setMoveDate(date);
              setMoveTime(time);
            }}
          />
          <div className="grid gap-x-3 sm:grid-cols-2">
            <TextInput
              label={t('appointmentActions.newDate')}
              type="date"
              value={moveDate}
              onChange={(event) => setMoveDate(event.target.value)}
            />
            <TextInput
              label={t('appointmentActions.newTime')}
              type="time"
              step={300}
              value={moveTime}
              onChange={(event) => setMoveTime(event.target.value)}
            />
          </div>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('appointmentActions.rescheduleNotice')}</p>
          <div className="flex gap-2">
            <Button variant="primary" busy={move.isPending} onClick={() => move.mutate()}>
              {t('appointmentActions.moveVisit')}
            </Button>
            <Button variant="ghost" onClick={() => setRescheduling(false)}>
              {t('appointmentActions.keepAsIs')}
            </Button>
          </div>
        </div>
      ) : null}

      {completing ? (
        <div className="mb-3 rounded-lg border border-[color:var(--color-line)] p-3">
          <div className="grid gap-x-3 sm:grid-cols-2">
            <TextInput
              label={t('appointmentActions.actualMinutesLabel')}
              hint={t('appointmentActions.actualMinutesHint', {
                scheduled: formatDuration(appointment.durationMinutes),
              })}
              type="number"
              min={5}
              max={6000}
              step={5}
              value={actualMinutes}
              onChange={(event) => setActualMinutes(event.target.value)}
            />
            {/* The money half of closing a visit out: the job ran long, or
                came in short, and this is the last moment before completion
                bills to say so. */}
            <MoneyInput
              label={t('appointmentActions.priceLabel')}
              hint={
                finalPriceNeeded
                  ? t('appointmentActions.priceHintFinal')
                  : pricedInCurrentCurrency
                    ? t('appointmentActions.priceHint')
                    : t('appointmentActions.priceHintRepriced', {
                        was: appointment.priceCurrencyCode,
                        now: currency,
                      })
              }
              {...(priceProblem ? { error: priceProblem } : {})}
              valueCents={priceCents}
              currencyCode={currency}
              onChangeCents={(cents) => {
                setPriceEdited(true);
                setPriceCents(cents);
              }}
              onInvalidChange={setPriceUnreadable}
            />
          </div>
          {photosBlocked ? (
            <Alert tone="warning">{t('checklists.missingPhotos', { count: missingPhotos })}</Alert>
          ) : null}
          {photoCheckFailed ? <Alert tone="error">{t('checklists.completionCheckFailed')}</Alert> : null}
          {photosBlocked && canOverridePhotos ? (
            <div className="my-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={photoOverride}
                  onChange={(event) => setPhotoOverride(event.target.checked)}
                />
                {t('checklists.completeAnyway')}
              </label>
              {photoOverride ? (
                <TextArea
                  label={t('checklists.overrideReason')}
                  value={photoOverrideReason}
                  onChange={(event) => setPhotoOverrideReason(event.target.value)}
                  maxLength={2000}
                />
              ) : null}
            </div>
          ) : null}
          <div className="flex gap-2">
            {/* Held back on an entry that is not a price rather than
                completing at the old one — or, for a negative, at nothing:
                what was typed is still on screen, and on an automatic
                schedule the click charges a card. */}
            <Button
              variant="primary"
              busy={complete.isPending}
              disabled={
                priceProblem !== null ||
                (finalPriceNeeded && correctedPrice === undefined) ||
                completionChecklist.isLoading ||
                photoCheckFailed ||
                (photosBlocked && (!canOverridePhotos || !photoOverride || !photoOverrideReason.trim()))
              }
              onClick={() => complete.mutate()}
            >
              {photosBlocked && photoOverride ? t('checklists.completeAnyway') : t('appointmentActions.markCompleted')}
            </Button>
            <Button variant="ghost" onClick={() => setCompleting(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {appointment.status !== 'confirmed' ? (
          <Button busy={confirm.isPending} onClick={() => confirm.mutate()}>
            {t('appointmentActions.markConfirmed')}
          </Button>
        ) : null}
        {!rescheduling ? (
          <Button
            onClick={() => {
              // Seed from the visit as it is *now*: after an earlier move the
              // mount-time values would quietly offer to move it back.
              setMoveDate(appointment.scheduledDate);
              setMoveTime(appointment.startTimeLocal);
              setRescheduling(true);
            }}
          >
            {t('appointmentActions.reschedule')}
          </Button>
        ) : null}
        {!completing ? (
          <Button
            onClick={() => {
              // Prefill with the scheduled length: on time is the usual case,
              // so recording it is a click, and correcting it is an edit. The
              // price is seeded the same way and for the same reason — what
              // the schedule charges is the answer on most visits.
              setActualMinutes(String(appointment.durationMinutes));
              setPriceCents(pricedInCurrentCurrency ? appointment.priceCents : null);
              setPriceEdited(false);
              setPriceUnreadable(false);
              setCompleting(true);
            }}
          >
            {t('appointmentActions.complete')}
          </Button>
        ) : null}
        <Button
          busy={noShow.isPending}
          onClick={() => {
            setNoShowError(null);
            setMarkingNoShow(true);
          }}
        >
          {t('appointmentActions.noShow')}
        </Button>
        <Button
          variant="danger"
          busy={cancel.isPending}
          onClick={() => {
            setCancelError(null);
            setCancellationInitiator('unknown');
            setCancellationReason('');
            setCanceling(true);
          }}
        >
          {t('appointmentActions.cancelVisit')}
        </Button>
        <Button variant="ghost" onClick={() => void copyLink()}>
          {copied ? t('appointmentActions.linkCopied') : t('appointmentActions.copyConfirmationLink')}
        </Button>
        {workOrderLink}
      </div>

      {markingNoShow ? (
        <ConfirmDialog
          title={t('appointmentActions.noShowDialogTitle')}
          confirmLabel={t('appointmentActions.noShow')}
          confirmVariant="danger"
          initialFocus="cancel"
          busy={noShow.isPending}
          error={noShowError}
          onConfirm={() => noShow.mutate()}
          onCancel={() => setMarkingNoShow(false)}
        >
          <p className="mb-4 text-sm">
            {t('appointmentActions.noShowDialogDescription', {
              customer: appointment.customerName,
              when: `${formatShortDate(appointment.scheduledDate)} ${formatLocalTime(appointment.startTimeLocal)}`,
            })}
          </p>
        </ConfirmDialog>
      ) : null}

      {canceling ? (
        <ConfirmDialog
          title={t('appointmentActions.cancelDialogTitle')}
          confirmLabel={t('appointmentActions.cancelVisit')}
          confirmVariant="danger"
          busy={cancelDialogBusy}
          error={cancelError}
          onConfirm={() => cancel.mutate(undefined)}
          onCancel={() => setCanceling(false)}
        >
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">
            {t('appointmentActions.cancelDialogDescription')}
          </p>
          {feeApplies && lateFeeNotice ? (
            <div className="mb-4">
              <p className="mb-3 text-sm">{lateFeeNotice}</p>
              {canTakePayment ? (
                noCard ? (
                  <>
                    <p className="mb-3 text-sm">{t('appointmentActions.lateFeeNoCard')}</p>
                    {lateFeeLinkNotice()}
                    <div className="flex flex-wrap gap-2">
                      {paymentsConfigured ? (
                        <Button
                          busy={sendPaymentLink.isPending}
                          disabled={cancel.isPending}
                          onClick={() => sendPaymentLink.mutate(true)}
                        >
                          {t('appointmentActions.cancelAndSendPaymentLink')}
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        busy={cancel.isPending}
                        disabled={sendPaymentLink.isPending}
                        onClick={() => {
                          const figures = askWaiveLateFee();
                          if (figures) cancel.mutate({ action: 'waive', figures });
                        }}
                      >
                        {t('appointmentActions.waiveFee')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {lateFeeCardChargeNotice()}
                    <div className="flex flex-wrap gap-2">
                      {/* Not primary: the dialog's own "Cancel visit" is its
                          answer, and this one also takes money. */}
                      <Button
                        busy={cancel.isPending}
                        disabled={
                          !feeAmount ||
                          !cardFeeDisclosureKnown ||
                          !paymentsConfigured ||
                          cancelDialogBusy ||
                          lateFeeFiguresRefreshing ||
                          feeBaseCents <= 0
                        }
                        onClick={() => {
                          const figures = askChargeLateFee();
                          if (figures) cancel.mutate({ action: 'charge', figures });
                        }}
                      >
                        {t('appointmentActions.chargeLateFee', { amount: chargeButtonAmount })}
                      </Button>
                      <Button
                        variant="ghost"
                        busy={cancel.isPending}
                        disabled={sendPaymentLink.isPending}
                        onClick={() => {
                          const figures = askWaiveLateFee();
                          if (figures) cancel.mutate({ action: 'waive', figures });
                        }}
                      >
                        {t('appointmentActions.waiveFee')}
                      </Button>
                    </div>
                  </>
                )
              ) : (
                <p className="text-sm text-[color:var(--color-muted)]">
                  {t('appointmentActions.lateFeeNeedsPaymentRole')}
                </p>
              )}
            </div>
          ) : null}
          <Select
            label={t('appointmentActions.cancellationInitiator')}
            value={cancellationInitiator}
            options={(['customer', 'company', 'unknown'] as const).map((value) => ({
              value,
              label: t(`appointmentActions.cancellationInitiators.${value}`),
            }))}
            onChange={(event) => setCancellationInitiator(event.target.value as 'customer' | 'company' | 'unknown')}
          />
          <TextArea
            label={t('appointmentActions.cancellationReason')}
            hint={t('appointmentActions.cancellationReasonHint')}
            maxLength={500}
            rows={3}
            value={cancellationReason}
            onChange={(event) => setCancellationReason(event.target.value)}
          />
        </ConfirmDialog>
      ) : null}

      <AppointmentPayments appointment={appointment} onChanged={onChanged} />
      <VisitPhotos appointmentId={appointment.id} />
    </div>
  );
}
